/**
 * Multi-Jurisdiction Cash Flow Management System - Type Definitions
 * Phase 1: Foundation & System Settings
 */

// Country Master Data
export interface Country {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  currency: string; // ISO 4217 currency code
  currencyName: string;
  currencySymbol: string;
  flag: string; // Emoji flag
  region: string;
}

// Jurisdiction (Active Country Configuration)
export interface Jurisdiction {
  id: string;
  countryCode: string;
  countryName: string;
  primaryCurrency: string;
  reportingCurrencies: string[]; // Additional currencies for reporting
  consolidationCurrency: string; // Currency used for group rollup
  isActive: boolean;
  statutoryPayments: StatutoryPayment[];
  createdAt: Date;
  updatedAt: Date;
}

// Statutory Payment Schedule
export interface StatutoryPayment {
  id: string;
  jurisdictionId: string;
  name: string;
  frequency: 'Monthly' | 'Quarterly' | 'Annual';
  dayOfMonth: number;
  nextDue: Date;
  isActive: boolean;
  // Future: amount will come from accounting system or be calculated
}

// Exchange Rate (from accounting system)
export interface ExchangeRate {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  effectiveDate: Date;
  source: string; // e.g., "Xero", "QuickBooks", "Manual"
  lastSynced: Date;
}

// System Settings for Multi-Jurisdiction
export interface CashFlowSettings {
  baseCurrency: string; // Group reporting currency
  multiJurisdictionMode: boolean; // Single vs Multiple countries
  defaultJurisdictionId?: string | null; // Default jurisdiction to select on load
  lastUpdated: Date;
}

// Statutory Payment Template (for pre-built templates)
export interface StatutoryPaymentTemplate {
  name: string;
  frequency: 'Monthly' | 'Quarterly' | 'Annual';
  dayOfMonth: number;
  description?: string;
}

// Country Template (pre-built statutory payments per country)
export interface CountryTemplate {
  countryCode: string;
  countryName: string;
  payments: StatutoryPaymentTemplate[];
}

// User Jurisdiction Access (Phase 5 - Permissions)
export interface UserJurisdictionAccess {
  userId: string;
  jurisdictionIds: string[]; // Empty array = no access, ['*'] = all jurisdictions
}

// Top 50 Countries Initial Data
export const TOP_50_COUNTRIES: Country[] = [
  // Europe
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', currencyName: 'British Pound Sterling', currencySymbol: '£', flag: '🇬🇧', region: 'Europe' },
  { code: 'DE', name: 'Germany', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇩🇪', region: 'Europe' },
  { code: 'FR', name: 'France', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇫🇷', region: 'Europe' },
  { code: 'IT', name: 'Italy', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇮🇹', region: 'Europe' },
  { code: 'ES', name: 'Spain', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇪🇸', region: 'Europe' },
  { code: 'NL', name: 'Netherlands', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇳🇱', region: 'Europe' },
  { code: 'CH', name: 'Switzerland', currency: 'CHF', currencyName: 'Swiss Franc', currencySymbol: 'CHF', flag: '🇨🇭', region: 'Europe' },
  { code: 'SE', name: 'Sweden', currency: 'SEK', currencyName: 'Swedish Krona', currencySymbol: 'kr', flag: '🇸🇪', region: 'Europe' },
  { code: 'BE', name: 'Belgium', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇧🇪', region: 'Europe' },
  { code: 'PL', name: 'Poland', currency: 'PLN', currencyName: 'Polish Zloty', currencySymbol: 'zł', flag: '🇵🇱', region: 'Europe' },
  { code: 'AT', name: 'Austria', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇦🇹', region: 'Europe' },
  { code: 'NO', name: 'Norway', currency: 'NOK', currencyName: 'Norwegian Krone', currencySymbol: 'kr', flag: '🇳🇴', region: 'Europe' },
  { code: 'DK', name: 'Denmark', currency: 'DKK', currencyName: 'Danish Krone', currencySymbol: 'kr', flag: '🇩🇰', region: 'Europe' },
  { code: 'FI', name: 'Finland', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇫🇮', region: 'Europe' },
  { code: 'IE', name: 'Ireland', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇮🇪', region: 'Europe' },
  { code: 'PT', name: 'Portugal', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇵🇹', region: 'Europe' },
  { code: 'GR', name: 'Greece', currency: 'EUR', currencyName: 'Euro', currencySymbol: '€', flag: '🇬🇷', region: 'Europe' },
  { code: 'CZ', name: 'Czech Republic', currency: 'CZK', currencyName: 'Czech Koruna', currencySymbol: 'Kč', flag: '🇨🇿', region: 'Europe' },
  { code: 'RO', name: 'Romania', currency: 'RON', currencyName: 'Romanian Leu', currencySymbol: 'lei', flag: '🇷🇴', region: 'Europe' },
  { code: 'HU', name: 'Hungary', currency: 'HUF', currencyName: 'Hungarian Forint', currencySymbol: 'Ft', flag: '🇭🇺', region: 'Europe' },
  
  // Americas
  { code: 'US', name: 'United States', currency: 'USD', currencyName: 'US Dollar', currencySymbol: '$', flag: '🇺🇸', region: 'Americas' },
  { code: 'CA', name: 'Canada', currency: 'CAD', currencyName: 'Canadian Dollar', currencySymbol: 'C$', flag: '🇨🇦', region: 'Americas' },
  { code: 'MX', name: 'Mexico', currency: 'MXN', currencyName: 'Mexican Peso', currencySymbol: 'MX$', flag: '🇲🇽', region: 'Americas' },
  { code: 'BR', name: 'Brazil', currency: 'BRL', currencyName: 'Brazilian Real', currencySymbol: 'R$', flag: '🇧🇷', region: 'Americas' },
  { code: 'AR', name: 'Argentina', currency: 'ARS', currencyName: 'Argentine Peso', currencySymbol: 'AR$', flag: '🇦🇷', region: 'Americas' },
  { code: 'CL', name: 'Chile', currency: 'CLP', currencyName: 'Chilean Peso', currencySymbol: 'CL$', flag: '🇨🇱', region: 'Americas' },
  { code: 'CO', name: 'Colombia', currency: 'COP', currencyName: 'Colombian Peso', currencySymbol: 'CO$', flag: '🇨🇴', region: 'Americas' },
  { code: 'PE', name: 'Peru', currency: 'PEN', currencyName: 'Peruvian Sol', currencySymbol: 'S/', flag: '🇵🇪', region: 'Americas' },
  
  // Asia-Pacific
  { code: 'AU', name: 'Australia', currency: 'AUD', currencyName: 'Australian Dollar', currencySymbol: 'A$', flag: '🇦🇺', region: 'Asia-Pacific' },
  { code: 'NZ', name: 'New Zealand', currency: 'NZD', currencyName: 'New Zealand Dollar', currencySymbol: 'NZ$', flag: '🇳🇿', region: 'Asia-Pacific' },
  { code: 'SG', name: 'Singapore', currency: 'SGD', currencyName: 'Singapore Dollar', currencySymbol: 'S$', flag: '🇸🇬', region: 'Asia-Pacific' },
  { code: 'HK', name: 'Hong Kong', currency: 'HKD', currencyName: 'Hong Kong Dollar', currencySymbol: 'HK$', flag: '🇭🇰', region: 'Asia-Pacific' },
  { code: 'JP', name: 'Japan', currency: 'JPY', currencyName: 'Japanese Yen', currencySymbol: '¥', flag: '🇯🇵', region: 'Asia-Pacific' },
  { code: 'CN', name: 'China', currency: 'CNY', currencyName: 'Chinese Yuan', currencySymbol: '¥', flag: '🇨🇳', region: 'Asia-Pacific' },
  { code: 'IN', name: 'India', currency: 'INR', currencyName: 'Indian Rupee', currencySymbol: '₹', flag: '🇮🇳', region: 'Asia-Pacific' },
  { code: 'KR', name: 'South Korea', currency: 'KRW', currencyName: 'South Korean Won', currencySymbol: '₩', flag: '🇰🇷', region: 'Asia-Pacific' },
  { code: 'TW', name: 'Taiwan', currency: 'TWD', currencyName: 'New Taiwan Dollar', currencySymbol: 'NT$', flag: '🇹🇼', region: 'Asia-Pacific' },
  { code: 'TH', name: 'Thailand', currency: 'THB', currencyName: 'Thai Baht', currencySymbol: '฿', flag: '🇹🇭', region: 'Asia-Pacific' },
  { code: 'MY', name: 'Malaysia', currency: 'MYR', currencyName: 'Malaysian Ringgit', currencySymbol: 'RM', flag: '🇲🇾', region: 'Asia-Pacific' },
  { code: 'ID', name: 'Indonesia', currency: 'IDR', currencyName: 'Indonesian Rupiah', currencySymbol: 'Rp', flag: '🇮🇩', region: 'Asia-Pacific' },
  { code: 'PH', name: 'Philippines', currency: 'PHP', currencyName: 'Philippine Peso', currencySymbol: '₱', flag: '🇵🇭', region: 'Asia-Pacific' },
  { code: 'VN', name: 'Vietnam', currency: 'VND', currencyName: 'Vietnamese Dong', currencySymbol: '₫', flag: '🇻🇳', region: 'Asia-Pacific' },
  
  // Middle East & Africa
  { code: 'AE', name: 'United Arab Emirates', currency: 'AED', currencyName: 'UAE Dirham', currencySymbol: 'د.إ', flag: '🇦🇪', region: 'Middle East' },
  { code: 'SA', name: 'Saudi Arabia', currency: 'SAR', currencyName: 'Saudi Riyal', currencySymbol: 'SR', flag: '🇸🇦', region: 'Middle East' },
  { code: 'IL', name: 'Israel', currency: 'ILS', currencyName: 'Israeli Shekel', currencySymbol: '₪', flag: '🇮🇱', region: 'Middle East' },
  { code: 'TR', name: 'Turkey', currency: 'TRY', currencyName: 'Turkish Lira', currencySymbol: '₺', flag: '🇹🇷', region: 'Middle East' },
  { code: 'ZA', name: 'South Africa', currency: 'ZAR', currencyName: 'South African Rand', currencySymbol: 'R', flag: '🇿🇦', region: 'Africa' },
  { code: 'EG', name: 'Egypt', currency: 'EGP', currencyName: 'Egyptian Pound', currencySymbol: 'E£', flag: '🇪🇬', region: 'Africa' },
  { code: 'NG', name: 'Nigeria', currency: 'NGN', currencyName: 'Nigerian Naira', currencySymbol: '₦', flag: '🇳🇬', region: 'Africa' },
  { code: 'KE', name: 'Kenya', currency: 'KES', currencyName: 'Kenyan Shilling', currencySymbol: 'KSh', flag: '🇰🇪', region: 'Africa' },
];

// Curated Statutory Payment Templates (UK, US, CA, AU)
export const STATUTORY_PAYMENT_TEMPLATES: Record<string, CountryTemplate> = {
  GB: {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    payments: [
      { name: 'VAT Return', frequency: 'Quarterly', dayOfMonth: 31, description: 'Value Added Tax quarterly return' },
      { name: 'PAYE & NIC (Employee & Employer)', frequency: 'Monthly', dayOfMonth: 22, description: 'Pay As You Earn and National Insurance Contributions' },
      { name: 'Corporation Tax', frequency: 'Annual', dayOfMonth: 31, description: 'Annual corporation tax payment' },
      { name: 'Workplace Pension Contributions', frequency: 'Monthly', dayOfMonth: 22, description: 'Employer pension contributions' },
      { name: 'CIS Deductions', frequency: 'Monthly', dayOfMonth: 22, description: 'Construction Industry Scheme deductions' },
    ],
  },
  US: {
    countryCode: 'US',
    countryName: 'United States',
    payments: [
      { name: 'Federal Income Tax', frequency: 'Quarterly', dayOfMonth: 15, description: 'Quarterly estimated tax payments' },
      { name: 'State Income Tax', frequency: 'Quarterly', dayOfMonth: 15, description: 'State quarterly estimated tax' },
      { name: 'Payroll Tax (FICA)', frequency: 'Monthly', dayOfMonth: 15, description: 'Federal Insurance Contributions Act taxes' },
      { name: 'Sales Tax', frequency: 'Monthly', dayOfMonth: 20, description: 'State and local sales tax' },
      { name: 'Unemployment Tax (FUTA)', frequency: 'Quarterly', dayOfMonth: 31, description: 'Federal unemployment tax' },
      { name: '401(k) Contributions', frequency: 'Monthly', dayOfMonth: 15, description: 'Employer retirement contributions' },
    ],
  },
  CA: {
    countryCode: 'CA',
    countryName: 'Canada',
    payments: [
      { name: 'GST/HST Return', frequency: 'Quarterly', dayOfMonth: 31, description: 'Goods and Services Tax / Harmonized Sales Tax' },
      { name: 'Payroll Remittances', frequency: 'Monthly', dayOfMonth: 15, description: 'Source deductions remittance' },
      { name: 'Corporate Income Tax', frequency: 'Annual', dayOfMonth: 30, description: 'Annual corporate tax payment' },
      { name: 'CPP & EI Contributions', frequency: 'Monthly', dayOfMonth: 15, description: 'Canada Pension Plan and Employment Insurance' },
      { name: 'Provincial Sales Tax', frequency: 'Monthly', dayOfMonth: 20, description: 'Provincial sales tax (where applicable)' },
    ],
  },
  AU: {
    countryCode: 'AU',
    countryName: 'Australia',
    payments: [
      { name: 'GST (BAS)', frequency: 'Quarterly', dayOfMonth: 28, description: 'Business Activity Statement - GST' },
      { name: 'PAYG Withholding', frequency: 'Monthly', dayOfMonth: 21, description: 'Pay As You Go withholding' },
      { name: 'Superannuation', frequency: 'Quarterly', dayOfMonth: 28, description: 'Superannuation guarantee contributions' },
      { name: 'Fringe Benefits Tax', frequency: 'Annual', dayOfMonth: 21, description: 'Annual FBT payment' },
      { name: 'Company Tax', frequency: 'Annual', dayOfMonth: 15, description: 'Annual company tax payment' },
    ],
  },
};

// Helper function to get country by code
export const getCountryByCode = (code: string): Country | undefined => {
  return TOP_50_COUNTRIES.find(c => c.code === code);
};

// Helper function to get countries by region
export const getCountriesByRegion = (region: string): Country[] => {
  return TOP_50_COUNTRIES.filter(c => c.region === region);
};

// Helper function to get all unique regions
export const getAllRegions = (): string[] => {
  return Array.from(new Set(TOP_50_COUNTRIES.map(c => c.region))).sort();
};

// Helper function to get template for country
export const getTemplateForCountry = (countryCode: string): CountryTemplate | undefined => {
  return STATUTORY_PAYMENT_TEMPLATES[countryCode];
};
